import type { CommunityAdminPersonSummary } from "@squeek/module-contracts/community-admin-v1";
import { ageInDays } from "./format";

export const NEW_X_DAYS = 90;
export const FEW_FOLLOWERS = 50;
export interface RiskSignals {
  xUsername: string | null;
  xCreatedAt: string | null;
  xFollowers: number | null;
  disqualified: boolean;
  flagged: boolean;
  invitesFrozen: boolean;
  criteriaFailures: readonly string[];
  fundingCluster?: boolean;
  walletCluster?: boolean;
  walletEmpty?: boolean | null;
  noMainnet?: boolean | null;
}
export type RiskLevel = "Disqualified" | "High" | "Watch" | "No signals";
export interface RiskAssessment { level: RiskLevel; reasons: string[] }

/** Review heuristic, never an eligibility decision. Unknown observations are not negative facts. */
export function assessRisk(signals: RiskSignals, now = Date.now()): RiskAssessment {
  const high: string[] = [];
  const watch: string[] = [];
  if (signals.flagged) high.push("Review flag");
  if (signals.fundingCluster) high.push("Funding cluster");
  if (signals.walletCluster) high.push("Wallet-history cluster");
  if (!signals.xUsername) watch.push("No X account");
  if (signals.xUsername) {
    const age = ageInDays(signals.xCreatedAt, now);
    if (age !== null && age < NEW_X_DAYS) watch.push(`X under ${NEW_X_DAYS} days`);
    if (signals.xFollowers !== null && signals.xFollowers < FEW_FOLLOWERS) watch.push(`Under ${FEW_FOLLOWERS} followers`);
  }
  if (signals.criteriaFailures.includes("x_default_picture")) watch.push("Default X picture");
  if (signals.walletEmpty === true) watch.push("No tx on scanned chains");
  else if (signals.noMainnet === true) watch.push("No Ethereum tx");
  if (signals.invitesFrozen) watch.push("Invites frozen");
  return {
    level: signals.disqualified ? "Disqualified" : high.length ? "High" : watch.length ? "Watch" : "No signals",
    reasons: [...(signals.disqualified ? ["Disqualified by moderation"] : []), ...high, ...watch],
  };
}

export function personRisk(person: CommunityAdminPersonSummary, now = Date.now()): RiskAssessment {
  return assessRisk({
    xUsername: person.x?.username ?? null, xCreatedAt: person.x?.createdAt ?? null,
    xFollowers: person.x?.followers ?? null, disqualified: person.disqualified !== null,
    flagged: person.flag !== null, invitesFrozen: person.invites.frozen !== null,
    criteriaFailures: person.criteriaFailures, fundingCluster: person.clusterId !== null,
    walletCluster: person.walletHistory.clusters.length > 0,
    walletEmpty: person.walletHistory.walletEmpty, noMainnet: person.walletHistory.noMainnet,
  }, now);
}
